import {Component, inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
    DxDataGridComponent,
    DxTemplateDirective,
    DxFormComponent,
} from "devextreme-angular";
import {
    DxiButtonComponent,
    DxiColumnComponent, DxiItemComponent, DxoColumnChooserComponent, DxoExportComponent, DxoFilterRowComponent,
    DxoHeaderFilterComponent, DxoLoadPanelComponent, DxoPagerComponent,
    DxoPagingComponent, DxoPositionComponent, DxoScrollingComponent, DxoSearchComponent, DxoSelectionComponent, DxoSortingComponent,
    DxoToolbarComponent
} from "devextreme-angular/ui/nested";
import {
    AccessType,
    AuthService,
    ChecklistCompliance,
    ExceptionType,
    HuddleInitiator,
    HuddleStatus,
    PermissionType,
    PunchInStatus,
    PunchOutStatus,
    TimeLogGridViewDto
} from "@services";
import {DxDataGridTypes} from "devextreme-angular/ui/data-grid";
import EnumUtils from "../../../shared/enum-utils";
import {UserAvatarEmailComponent} from "../../../components/utils/user-avatar-email/user-avatar-email.component";
import {FilterPanelComponent} from "../../../components/library/filter-panel/filter-panel.component";
import {ClearExceptionsComponent} from "./clear-exceptions/clear-exceptions.component";
import {Button, QuickViewPanelComponent} from "../../../components/library/quick-view-panel/quick-view-panel.component";
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {ContactStatusComponent} from "../../../components";
import {DasherizePipe} from "../../../pipes/dasherize.pipe";
import {CurrencyPipe, DatePipe} from "@angular/common";
import {ToDatePipe} from "../../../pipes/to-date.pipe";
import {FilterConfig, GridPageComponent} from "../../common/grid-page.component";
import {RouterLink} from "@angular/router";

@Component({
    selector: 'app-time-logs',
    imports: [
        DxDataGridComponent,
        DxiColumnComponent,
        DxoHeaderFilterComponent,
        DxoScrollingComponent,
        DxTemplateDirective,
        DxiItemComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        UserAvatarEmailComponent,
        DxFormComponent,
        FilterPanelComponent,
        ClearExceptionsComponent,
        QuickViewPanelComponent,
        CapitalCasePipe,
        ContactStatusComponent,
        DasherizePipe,
        DatePipe,
        ToDatePipe,
        DxiButtonComponent,
        CurrencyPipe,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        RouterLink
    ],
    templateUrl: './time-logs.component.html'
})
export class TimeLogsComponent extends GridPageComponent<TimeLogGridViewDto> implements OnInit, OnChanges {
    protected auth = inject(AuthService);

    @Input() employeeId: string;
    @Input() offset: number = 100;
    @Input() embedded: boolean = false;

    dataSource = this.dataSources.timeLogs(null);
    protected override exportName: string = "Time Logs";

    protected buttons: Button[] = [];

    constructor() {
        super();
        this.init(TimeLogGridViewDto);
    }

    ngOnInit(): void {
        if (this.auth.checkPermission(PermissionType.EditTimeLogs, AccessType.Manage)) {
            this.buttons.push({
                text: 'Clear Exceptions',
                icon: 'clearformat',
                onClick: () => {
                    this.isClearExceptionsPopupOpened = true;
                }
            })
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['employeeId']) {
            this.dataSource = this.dataSources.timeLogs(this.employeeId);
        }
    }

    isQuickViewPanelOpened: boolean = false;
    isClearExceptionsPopupOpened = false;

    clockInStatusOptions = EnumUtils.getEnumOptions(PunchInStatus);
    clockOutStatusOptions = EnumUtils.getEnumOptions(PunchOutStatus);
    huddleStatusOptions = EnumUtils.getEnumOptions(HuddleStatus);
    exceptionTypeOptions = EnumUtils.getEnumOptions(ExceptionType);
    checklistComplianceOptions = EnumUtils.getEnumOptions(ChecklistCompliance);

    protected override filterConfig: FilterConfig[] = [
        { field: 'employee.name', op: 'contains' },
        { field: 'punchDate', op: ">="},
        { field: 'punchInStatus' },
        { field: 'punchOutStatus' },
        { field: 'huddleStatus' },
        { field: 'shiftCode', op: 'contains' },
        { field: 'hasMealTicket', op: '=' },
        { field: 'exceptionType', op: '=' },
        { field: 'clearingExceptionReason', op: 'contains' }
    ];

    override onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        this.selectedRow = {...e.data} as TimeLogGridViewDto;
        this.isQuickViewPanelOpened = true;
    }

    protected isException() {
        return this.selectedRow != null && this.selectedRow.exceptionType != ExceptionType.None;
    }

    protected async onExceptionCleared() {
        await this.dataGrid.instance.refresh();
        if (this.selectedRow) {
            const data = await this.dataSources.timeLogs(this.employeeId).load({filter: ['id', '=', this.selectedRow.id]}) as TimeLogGridViewDto[];
            this.selectedRow = data.length > 0 ? data[0] : null;
        }
    }

    isEditVisible = (e: { row: { data: TimeLogGridViewDto } }) => {
        return e.row.data.exceptionType !== ExceptionType.None &&
         this.auth.checkPermission(PermissionType.EditTimeLogs, AccessType.Manage);
    }

    override onEdit = (e: any) => {
        const event = e as { row: { data: TimeLogGridViewDto } };
        this.selectedRow = {...event.row.data} as TimeLogGridViewDto;
        this.isClearExceptionsPopupOpened = true;
    }

    override onView = (e: any) => {
        const event = e as { row: { data: TimeLogGridViewDto } };
        this.selectedRow = {...event.row.data} as TimeLogGridViewDto;
        this.isQuickViewPanelOpened = true;
    }

    protected readonly HuddleInitiator = HuddleInitiator;
    protected readonly ExceptionType = ExceptionType;
    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;
    protected readonly ChecklistCompliance = ChecklistCompliance;
}
